import { SHARED_ONLY_KEYS, readSharedPref, sharedStoreActive, writeSharedPref } from '../hooks/sharedPrefs';
import { parkHandoff } from './facilityWindow';

/**
 * PSI staff: the facilities they used most recently in ANY connection (hooks/sharedPrefs.ts), so
 * the facility switcher can take them straight back to a facility of another customer.
 *
 * Navigation, not a setting: recorded and shown in every connection, including one where the PSI
 * user turned the shared settings off on the Settings page (sharedStoreActive, not
 * sharedPrefsActive).
 *
 * Kept beside, not instead of, the per-connection list (utils/recentFacilities.ts), which the
 * sign-in resolver uses and which stays as it is. Each entry carries its connection: a facility
 * number means nothing outside its own database.
 *
 * Picking one from another connection uses the existing "open this connection and facility" path
 * (utils/facilityWindow.ts): the request is parked, the organization picker honours the connection
 * only if the server lists it for this user, and the toolbar honours the facility only if it is in
 * this user's facility list there. So this list grants nothing; it is a shortcut.
 */

export interface CrossConnectionRecent {
  connectionId: number;
  connectionName: string;
  facilityId: number;
  facilityName: string;
  /** When it was last opened (ms). */
  lastUsed: number;
}

/** How many are kept. */
export const MAX_CROSS_CONNECTION_RECENTS = 8;

function isEntry(e: unknown): e is CrossConnectionRecent {
  if (!e || typeof e !== 'object') return false;
  const r = e as CrossConnectionRecent;
  return typeof r.connectionId === 'number' && r.connectionId > 0
    && typeof r.facilityId === 'number' && r.facilityId > 0
    && typeof r.facilityName === 'string'
    && typeof r.lastUsed === 'number';
}

/** The stored list, newest first, bad entries dropped. */
export function parseCrossConnectionRecents(raw: unknown): CrossConnectionRecent[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(isEntry)
    .map((r) => ({ ...r, connectionName: typeof r.connectionName === 'string' ? r.connectionName : '' }))
    .sort((a, b) => b.lastUsed - a.lastUsed);
}

/** The list with this facility put first (once), cut to the maximum. */
export function withCrossConnectionRecent(
  list: readonly CrossConnectionRecent[],
  entry: CrossConnectionRecent,
): CrossConnectionRecent[] {
  const rest = list.filter((r) => !(r.connectionId === entry.connectionId && r.facilityId === entry.facilityId));
  return [entry, ...rest].sort((a, b) => b.lastUsed - a.lastUsed).slice(0, MAX_CROSS_CONNECTION_RECENTS);
}

/** The entries of connections other than this one, newest first. */
export function otherConnectionRecents(
  list: readonly CrossConnectionRecent[],
  currentConnectionId: number | null | undefined,
): CrossConnectionRecent[] {
  return list.filter((r) => r.connectionId !== currentConnectionId);
}

/** The PSI user's list (empty for everyone else). */
export function readCrossConnectionRecents(): CrossConnectionRecent[] {
  if (!sharedStoreActive()) return [];
  return parseCrossConnectionRecents(readSharedPref<unknown>(SHARED_ONLY_KEYS.RECENT_FACILITIES, []));
}

/** Records a facility the PSI user is now in. Does nothing for anyone else. */
export function recordCrossConnectionRecent(
  connectionId: number | null | undefined,
  connectionName: string | null | undefined,
  facilityId: number,
  facilityName: string,
  now: number = Date.now(),
): void {
  if (!sharedStoreActive() || !connectionId || connectionId <= 0 || facilityId <= 0) return;
  const next = withCrossConnectionRecent(readCrossConnectionRecents(), {
    connectionId,
    connectionName: connectionName ?? '',
    facilityId,
    facilityName,
    lastUsed: now,
  });
  writeSharedPref(SHARED_ONLY_KEYS.RECENT_FACILITIES, next);
}

/**
 * Goes to a facility of another connection in this window: parks the request and opens the
 * organization picker, which switches the connection (when the server allows it) exactly as a pick
 * there does; the toolbar then opens the facility (when this user has it there).
 */
export function switchToCrossConnectionRecent(
  entry: Pick<CrossConnectionRecent, 'connectionId' | 'facilityId'>,
  navigate: (to: string) => void,
): void {
  parkHandoff({ connectionId: entry.connectionId, facilityId: entry.facilityId });
  navigate('/connection?switch=true');
}
